import { defineStore } from 'pinia';
import { nextTick, ref } from 'vue';
import { publicService } from '~/services/public-service';
import { setLicenseStats } from '~/types/licenses/licenses';

// Type definitions for the stats data
interface CompanyStats {
    data_brokers: number;
    data_broker_types: Record<string, number>;
    custom_sites: number;
}

export const useGlobalStore = defineStore('global', () => {
    // Stats caching with proper typing
    const statsData = ref<CompanyStats | null>(null);
    const statsCacheTimestamp = ref<number>(0);

    async function fetchCompanyStats() {
        // Check if cache is still valid (1 hour)
        const now = Date.now();
        const cacheExpiry = 60 * 60 * 1000; // 1 hour in milliseconds
        
        if (statsCacheTimestamp.value && (now - statsCacheTimestamp.value) < cacheExpiry) {
            // Use cached stats
            if (statsData.value) {
                setLicenseStats(statsData.value.data_brokers, statsData.value.custom_sites);
                return;
            }
        }
        
        // Fetch new stats
        const stats = await publicService.getCompanyStats();
        statsData.value = stats;
        statsCacheTimestamp.value = now;
        setLicenseStats(stats.data_brokers, stats.custom_sites);
    }

    // Initialize stats after store is fully set up with persisted data
    nextTick(() => {
        fetchCompanyStats();
    });

    return {
        statsData,
        statsCacheTimestamp,
        fetchCompanyStats
    };
}, {
    persist: {
        pick: ['statsData', 'statsCacheTimestamp']
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useGlobalStore, import.meta.hot));
}
